import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Camera, ArrowRight, Coins, Clock, History } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/scans");
        setScans(data);
      } catch (err) {
        console.error("Failed to load scans:", err);
      }
      setLoading(false);
    })();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16 animate-fade-in">
      <p className="cm-label">DASHBOARD</p>
      <h1 className="cm-heading text-4xl sm:text-5xl mt-3" data-testid="dashboard-greeting">
        Hey <span className="text-copper">{user?.name || "there"}</span>.
      </h1>
      <p className="mt-3 text-gray-400 max-w-xl">
        Drop a photo, get your script. Below is what you've scanned so far.
      </p>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* big CTA card */}
        <Link
          to="/scan"
          data-testid="dashboard-new-scan-card"
          className="cm-card p-8 md:col-span-7 md:row-span-2 flex flex-col justify-between hover:-translate-y-1 hover:border-copper/50 transition-all"
        >
          <div>
            <Camera className="w-7 h-7 text-copper" />
            <h2 className="cm-heading text-3xl sm:text-4xl mt-6">Scan a haircut</h2>
            <p className="text-gray-400 mt-3 max-w-md">
              Upload any haircut photo — we'll name it, script it, and shop it.
            </p>
          </div>
          <div className="flex items-center gap-2 text-copper font-semibold tracking-wide mt-8">
            START SCAN <ArrowRight className="w-4 h-4" />
          </div>
        </Link>

        <div className="cm-card p-8 md:col-span-5">
          <Coins className="w-6 h-6 text-copper" />
          <p className="cm-label mt-5">Scans today</p>
          <p className="cm-heading text-5xl mt-2" data-testid="dashboard-credits">
            {(user?.scans_used_today ?? 0)} <span className="text-2xl text-gray-500">/ {user?.daily_limit ?? 5}</span>
          </p>
          <p className="text-xs text-gray-500 mt-3">Rolling {user?.window_hours ?? 12}h window</p>
        </div>

        <div className="cm-card p-8 md:col-span-5">
          <History className="w-6 h-6 text-copper" />
          <p className="cm-label mt-5">Total scans</p>
          <p className="cm-heading text-5xl mt-2">{scans.length}</p>
          <Link to="/history" className="cm-btn-secondary mt-6 inline-flex" data-testid="dashboard-history-link">
            View history
          </Link>
        </div>
      </div>

      <div className="mt-16">
        <div className="flex items-center justify-between mb-6">
          <h3 className="cm-heading text-2xl">Recent scans</h3>
          <Link to="/history" className="cm-label text-copper hover:underline">View all</Link>
        </div>
        {loading ? (
          <p className="text-gray-500">Loading…</p>
        ) : scans.length === 0 ? (
          <div className="cm-card p-10 text-center">
            <Clock className="w-7 h-7 text-gray-500 mx-auto" />
            <p className="text-gray-400 mt-4">No scans yet. Try your first one — it's free.</p>
            <Link to="/scan" className="cm-btn-primary inline-flex mt-6">Start now</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {scans.slice(0, 8).map((s) => (
              <Link
                key={s.id}
                to={`/scan/${s.id}`}
                data-testid={`dashboard-scan-${s.id}`}
                className="cm-card p-3 hover:-translate-y-1 hover:border-copper/50 transition-all"
              >
                <div className="aspect-square overflow-hidden rounded-md bg-ink-800">
                  <img
                    src={fileUrl(s.storage_path)}
                    alt={s.ai_result?.style_name || "scan"}
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="mt-3 text-sm font-semibold truncate">
                  {s.ai_result?.style_name || (s.ai_result?.error ? "Unrecognized" : "Untitled")}
                </p>
                <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">
                  {new Date(s.created_at).toLocaleDateString()}
                </p>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
